import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    SalutationBreadcrumbs,
    SalutationCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as SalutationActions from "../../../actions/salutations";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./SalutationPage.scss";

interface SalutationPageProps {
    form: Dtos.Salutation & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    salutation: Dtos.Salutation;
    lookups: Dtos.Lookup[];
    loadingSalutation: boolean;
    loadSalutationFailure: boolean;
    loadSalutationErrorCode: string;
    savingSalutation: boolean;
    saveSalutationSuccess: boolean;
    saveSalutationFailure: boolean;
    creatingSalutation: boolean;
    createSalutationSuccess: boolean;
    validatingSalutation: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearSalutation: SalutationActions.ISalutationClearActionCreator;
    loadSalutation: SalutationActions.ISalutationLoadByIdActionCreator;
    saveSalutation: SalutationActions.ISalutationSaveActionCreator;
    createSalutation: SalutationActions.ISalutationCreateActionCreator;
    validateSalutation: SalutationActions.ISalutationValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.salutation";

const SalutationPage: FC<SalutationPageProps> = ({
    form,
    formState,
    formProperties,
    salutation,
    lookups,
    loadingSalutation,
    loadSalutationFailure,
    loadSalutationErrorCode,
    savingSalutation,
    saveSalutationSuccess,
    saveSalutationFailure,
    creatingSalutation,
    createSalutationSuccess,
    validatingSalutation,
    validationFailures,
    permissions,
    clearSalutation,
    loadSalutation,
    saveSalutation,
    createSalutation,
    validateSalutation,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { salutationId } = useParams<{ salutationId: string }>();
    const salutationIdNumber = parseInt(salutationId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Salutation = {} as Dtos.Salutation;

        loadForm(reduxFormName, Object.assign({ ...salutation }, extraData));
    };


    // Load Salutation
    useEffect(() => {
        if (salutationIdNumber && !salutation && !loadingSalutation) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(SalutationBreadcrumbs(salutationIdNumber));
            loadSalutation(salutationIdNumber);
        } else if (
            !salutationIdNumber &&
            !creatingSalutation &&
            !createSalutationSuccess
        ) {
            createSalutation();
            setBreadcrumbs(SalutationCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (salutation && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        salutationIdNumber,
        salutation,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        SalutationBreadcrumbs,
        loadSalutation,
        createSalutation,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingSalutation,
        creatingSalutation,
        createSalutationSuccess,
        SalutationCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadSalutationFailure) {
            navigate(`/error/${loadSalutationErrorCode}`);
        }
    }, [loadSalutationFailure, loadSalutationErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (salutationIdNumber && salutation?.value) {
            setBreadcrumbs(
                SalutationBreadcrumbs(salutationIdNumber, salutation?.value)
            );
        }
    }, [salutationIdNumber, salutation, setBreadcrumbs, SalutationBreadcrumbs]);

    // Reload salutation after saving
    useEffect(() => {
        if (!savingSalutation && saveSalutationSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Salutation saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/salutations";
                navigate(url);
            } else if (!salutationIdNumber) {
                let url: string = `/settings/general/salutations/${salutation?.id}`;
                navigate(url);
                clearSalutation();
            }
        }
    }, [
        savingSalutation,
        saveSalutationSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingSalutation && saveSalutationFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingSalutation, saveSalutationFailure]);

    // Cleanup effect to clear the salutation on component unmount
    useEffect(() => {
        return () => {
            clearSalutation();
            resetForm(reduxFormName);
        };
    }, [clearSalutation, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/salutations";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Salutation) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Salutation = {} as Dtos.Salutation;

            await saveSalutation(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Salutation = {} as Dtos.Salutation;

            if (form && hasBeenSubmitted && !validatingSalutation) {
                await validateSalutation(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.SalutationsUpdate,
                Dtos.Permission.SalutationsCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingSalutation ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "salutation",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "salutation",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "salutation",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "salutation",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingSalutation,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingSalutation}
            permission={Dtos.Permission.SalutationsView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<SalutationPageProps> => {
    let salutation: Dtos.Salutation | undefined = !(
        state.salutations.formData instanceof Array
    )
        ? state.salutations.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        salutation: salutation!,
        loadingSalutation:
            state.salutations.loadState &&
            state.salutations.loadState.status == RequestState.Pending,
        loadSalutationFailure:
            state.salutations.loadState &&
            state.salutations.loadState.status == RequestState.Failure,
        loadSalutationErrorCode: state.salutations.loadState.errorCode,
        savingSalutation:
            state.salutations.saveState &&
            state.salutations.saveState.status == RequestState.Pending,
        saveSalutationSuccess:
            state.salutations.saveState &&
            state.salutations.saveState.status == RequestState.Success,
        saveSalutationFailure:
            state.salutations.saveState &&
            state.salutations.saveState.status == RequestState.Failure,
        creatingSalutation:
            state.salutations.createState &&
            state.salutations.createState.status == RequestState.Pending,
        createSalutationSuccess:
            state.salutations.createState &&
            state.salutations.createState.status == RequestState.Success,
        validatingSalutation:
            state.salutations.validateState &&
            state.salutations.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.salutation,
        formState: state.salutations.formState,
        formProperties: state.salutations.formProperties,
        validationFailures: salutation
            ? state.salutations.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<SalutationPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadSalutation: bindActionCreators(
            SalutationActions.LoadSalutationById,
            dispatch
        ),
        clearSalutation: bindActionCreators(SalutationActions.Clear, dispatch),
        saveSalutation: bindActionCreators(
            SalutationActions.SaveSalutation,
            dispatch
        ),
        createSalutation: bindActionCreators(
            SalutationActions.CreateSalutation,
            dispatch
        ),
        validateSalutation: bindActionCreators(
            SalutationActions.ValidateSalutation,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SalutationPage);
